import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import uuid from 'uuid/v4';
import {Map} from 'immutable';
import classnames from 'classnames';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import PureComponent from 'lib/purecomponent';
import {getStoreByName} from 'client/framework';
import createLocalStore, {localStorePropType} from 'lib/decorators/createLocalStore';
import makeRef from 'lib/makeRef';
import './audio-player.scss';

export default class AudioPlayer extends PureComponent {
  static propTypes = {
    caption: PropTypes.string,
    src: PropTypes.string,
    inline: PropTypes.bool
  };

  getStoreName() {
    let storeName = `AudioPlayerStore/${this.props.src}`;
    /**
     * This is here in the off chance someone is using multiple AudioPlayers with the same
     * source on the page. This should be an edge case, but if it does happen, it won't affect
     * functionality.
     */
    if (getStoreByName(storeName)) {
      storeName += uuid();
    }
    return storeName;
  }

  render() {
    const {src, inline, caption} = this.props;
    return (
      <AudioPlayerHandler
        caption={caption}
        src={src}
        inline={inline}
        storeName={this.getStoreName()}
      />
    );
  }
}

@createLocalStore(
  new Map({
    isPlaying: false,
    isMuted: false
  })
)
class AudioPlayerHandler extends React.Component {
  static propTypes = {
    caption: PropTypes.string,
    setProperty: PropTypes.func,
    store: localStorePropType,
    src: PropTypes.string,
    inline: PropTypes.bool
  };

  constructor() {
    super();
    this.audioNode = null;
    this.currentTimeNode = null;
    this.durationNode = null;
    this.progressBarNode = null;
  }

  componentDidMount() {
    this.audioNode.addEventListener('ended', this.handleEnded);
    this.audioNode.addEventListener('loadedmetadata', this.handleLoadedMetadata);
  }

  handleEnded = () => {
    this.props.setProperty('isPlaying', false);
  };

  handleLoadedMetadata = () => {
    this.forceUpdate();
  };

  togglePlay = () => {
    const newPlayingVal = !this.props.store.get('isPlaying');
    this.props.setProperty('isPlaying', newPlayingVal);
    if (newPlayingVal === true) {
      this.audioNode.play();
    } else {
      this.audioNode.pause();
    }
  };

  toggleMute = () => {
    const newMuteVal = !this.props.store.get('isMuted');
    this.props.setProperty('isMuted', newMuteVal);
    if (newMuteVal === true) {
      this.audioNode.volume = 0;
    } else {
      this.audioNode.volume = 1;
    }
  };

  makeRef = makeRef.bind(this);

  render() {
    const isPlaying = this.props.store.get('isPlaying');
    const isMuted = this.props.store.get('isMuted');
    const audioNode = this.audioNode;
    let caption = null;
    if (this.props.caption) {
      caption = <MarkdownRendererV2 className='audio-player__caption' text={this.props.caption} />;
    }
    return (
      <div
        className={classnames('audio-player', {
          'audio-player--inline': this.props.inline
        })}
      >
        <div className='audio-player__content-wrapper'>
          <div className='audio-player__controls'>
            <audio
              src={this.props.src}
              ref={this.makeRef}
              data-ref-name='audioNode'
              controls={false}
            />
            <button className='unbutton audio-player__play-pause-btn' onClick={this.togglePlay}>
              <span className={classnames('fa fa-fw', isPlaying ? 'fa-pause' : 'fa-play')} />
              <span className='audio-player__label'>Play</span>
            </button>
            {audioNode ? <AudioProgress audioNode={audioNode} /> : null}
            <button className='unbutton audio-player__mute-btn' onClick={this.toggleMute}>
              <span
                className={classnames('fa fa-fw', isMuted ? 'fa-volume-up' : 'fa-volume-off')}
              />
              <span className='audio-player__label'>Mute</span>
            </button>
          </div>
          {audioNode ? <TimeElapsed audioNode={audioNode} /> : null}
        </div>
        {caption}
      </div>
    );
  }
}

class AudioProgress extends React.Component {
  static propTypes = {
    audioNode: PropTypes.any
  };

  componentDidMount() {
    this.props.audioNode.addEventListener('timeupdate', this.update);
  }

  componentWillUnmount() {
    this.props.audioNode.removeEventListener('timeupdate', this.update);
  }

  update = () => this.forceUpdate();

  getValue(audioNode) {
    // Ready state 2 means we don't have enough data to play more than one frame of the audio
    if (audioNode.readyState <= 2) {
      return 0;
    }
    return (audioNode.currentTime / audioNode.duration) * 100;
  }

  render() {
    const audioNode = this.props.audioNode;
    if (!audioNode) {
      return null;
    }
    return (
      <progress
        className='audio-progress-meter progress-meter progress-meter--medium'
        value={this.getValue(audioNode)}
        max={100}
      />
    );
  }
}

class TimeElapsed extends React.Component {
  static propTypes = {
    audioNode: PropTypes.any
  };

  componentDidMount() {
    this.props.audioNode.addEventListener('timeupdate', this.update);
  }

  componentWillUnmount() {
    this.props.audioNode.removeEventListener('timeupdate', this.update);
  }

  update = () => this.forceUpdate();

  formatSeconds(seconds) {
    return moment.utc(seconds * 1000).format('mm:ss');
  }

  render() {
    const audioNode = this.props.audioNode;
    if (!audioNode) {
      return null;
    }
    const content =
      audioNode.readyState >= 1 // Ready state 1 or above means we have valid metadata
        ? `${this.formatSeconds(audioNode.currentTime)} / ${this.formatSeconds(audioNode.duration)}`
        : String.fromCharCode(160); // non-breaking space
    return <div className='audio-player-time-elapsed'>{content}</div>;
  }
}
